import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { clientsAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Search,
    Users,
    RefreshCw,
    AlertTriangle,
    Phone,
    MapPin,
    ShoppingBag,
    Eye,
    UserX,
    UserCheck,
    StickyNote,
    DollarSign,
    Gift,
    Star,
    Upload,
    FileSpreadsheet
} from 'lucide-react';

export default function Clients() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [clientOrders, setClientOrders] = useState([]);
    const [loadingClient, setLoadingClient] = useState(false);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [clientNote, setClientNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importing, setImporting] = useState(false);

    const fetchClients = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (search) params.search = search;
            
            const response = await clientsAPI.getAll(params);
            setClients(response.data);
        } catch (error) {
            console.error('Error fetching clients:', error);
            toast.error('Erreur lors du chargement des clients');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const handleOpenClientDetail = async (client) => {
        setSelectedClient(client);
        setClientDialogOpen(true);
        setLoadingClient(true);
        
        try {
            const response = await clientsAPI.getOne(client.phone);
            setClientOrders(response.data.orders || []);
            setSelectedClient(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des détails');
        } finally {
            setLoadingClient(false);
        }
    };

    const handleToggleMauvaisClient = async (client) => {
        const newStatus = !client.is_mauvais_client;
        
        try {
            await clientsAPI.update(client.phone, { is_mauvais_client: newStatus });
            toast.success(newStatus ? 'Client marqué comme Mauvais Client' : 'Tag Mauvais Client retiré');
            fetchClients();
            
            if (selectedClient && selectedClient.phone === client.phone) {
                setSelectedClient({ ...selectedClient, is_mauvais_client: newStatus });
            }
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleOpenNoteDialog = (client) => {
        setSelectedClient(client);
        setClientNote(client.note || '');
        setNoteDialogOpen(true);
    };

    const handleSaveNote = async () => {
        if (!selectedClient) return;
        setSavingNote(true);
        
        try {
            await clientsAPI.update(selectedClient.phone, { note: clientNote });
            toast.success('Note sauvegardée');
            setNoteDialogOpen(false);
            fetchClients();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSavingNote(false);
        }
    };

    const handleImportCSV = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        setImporting(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/import-csv`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });
            
            const data = await response.json();
            
            if (data.success) {
                toast.success(`Import terminé: ${data.imported} nouveaux, ${data.updated} mis à jour`);
                if (data.errors?.length > 0) {
                    toast.warning(`${data.errors.length} erreur(s) lors de l'import`);
                }
                fetchClients();
            } else {
                toast.error(data.detail || 'Erreur lors de l\'import');
            }
        } catch (error) {
            toast.error('Erreur lors de l\'import du fichier');
        } finally {
            setImporting(false);
            setImportDialogOpen(false);
            event.target.value = '';
        }
    };

    // Stats
    const totalClients = clients.length;
    const mauvaisClients = clients.filter(c => c.is_mauvais_client).length;
    const totalRevenue = clients.reduce((sum, c) => sum + (c.total_spent || 0), 0);

    return (
        <Layout>
            <div className="space-y-6" data-testid="clients-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">Clients</h1>
                        <p className="text-muted-foreground mt-1">
                            Gérez vos clients et leur historique
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setImportDialogOpen(true)}
                            data-testid="import-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Importer CSV
                        </Button>
                        <Button
                            variant="outline"
                            onClick={fetchClients}
                            disabled={loading}
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                            Actualiser
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="card-luxury">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Clients</p>
                                    <p className="text-2xl font-bold">{totalClients}</p>
                                </div>
                                <Users className="w-8 h-8 text-primary opacity-80" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="card-luxury">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Mauvais Clients</p>
                                    <p className="text-2xl font-bold text-red-600">{mauvaisClients}</p>
                                </div>
                                <AlertTriangle className="w-8 h-8 text-red-500 opacity-80" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="card-luxury">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Chiffre d'Affaires Total</p>
                                    <p className="text-2xl font-bold">{formatCurrency(totalRevenue)}</p>
                                </div>
                                <DollarSign className="w-8 h-8 text-emerald-500 opacity-80" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Search */}
                <Card className="card-luxury">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher par nom ou téléphone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 input-luxury"
                                data-testid="search-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Clients Table */}
                <Card className="card-luxury">
                    <CardContent className="p-0">
                        <div className="table-container">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="table-header">Client</TableHead>
                                        <TableHead className="table-header">Téléphone</TableHead>
                                        <TableHead className="table-header">Adresse</TableHead>
                                        <TableHead className="table-header">Commandes</TableHead>
                                        <TableHead className="table-header">Total Dépensé</TableHead>
                                        <TableHead className="table-header">Points</TableHead>
                                        <TableHead className="table-header">Statut</TableHead>
                                        <TableHead className="table-header">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-12">
                                                <div className="flex justify-center">
                                                    <div className="spinner" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : clients.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                                                Aucun client trouvé
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        clients.map((client) => (
                                            <TableRow 
                                                key={client.phone} 
                                                className={cn(
                                                    "table-row",
                                                    client.is_mauvais_client && "bg-red-50 hover:bg-red-100"
                                                )}
                                            >
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{client.full_name}</span>
                                                        {client.note && (
                                                            <StickyNote className="w-4 h-4 text-amber-500" />
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <Phone className="w-3 h-3" />
                                                        {client.phone}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm max-w-[200px] truncate block" title={client.adresse}>
                                                        {client.adresse || '-'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                                                        <span className="font-medium">{client.total_orders}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-semibold">
                                                    {formatCurrency(client.total_spent)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Gift className="w-4 h-4 text-amber-500" />
                                                        <span className="font-semibold text-amber-600">
                                                            {client.reward_points || 0}
                                                        </span>
                                                        {(client.reward_points || 0) > 0 && (
                                                            <span className="text-xs text-muted-foreground">
                                                                ({client.reward_points} DA)
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {client.is_mauvais_client ? (
                                                        <Badge className="bg-red-100 text-red-700 border-red-200">
                                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                                            Mauvais Client
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                                                            <UserCheck className="w-3 h-3 mr-1" />
                                                            Normal
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleOpenClientDetail(client)}
                                                            title="Voir détails"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleOpenNoteDialog(client)}
                                                            title="Ajouter note"
                                                        >
                                                            <StickyNote className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleToggleMauvaisClient(client)}
                                                            title={client.is_mauvais_client ? "Retirer tag" : "Marquer mauvais client"}
                                                            className={client.is_mauvais_client ? "text-emerald-600" : "text-red-600"}
                                                        >
                                                            {client.is_mauvais_client ? (
                                                                <UserCheck className="w-4 h-4" />
                                                            ) : (
                                                                <UserX className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {/* Client Detail Dialog */}
                <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Détails Client
                                {selectedClient?.is_mauvais_client && (
                                    <Badge className="bg-red-100 text-red-700 border-red-200 ml-2">
                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                        Mauvais Client - Paiement à l'avance requis
                                    </Badge>
                                )}
                            </DialogTitle>
                        </DialogHeader>
                        
                        {selectedClient && (
                            <div className="space-y-4">
                                {/* Client Info */}
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Nom</p>
                                                <p className="font-medium">{selectedClient.full_name}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Téléphone</p>
                                                <p className="font-medium">{selectedClient.phone}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Commandes</p>
                                                <p className="font-medium">{selectedClient.total_orders}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Total Dépensé</p>
                                                <p className="font-medium">{formatCurrency(selectedClient.total_spent)}</p>
                                            </div>
                                        </div>
                                        {selectedClient.note && (
                                            <div className="mt-4 p-3 bg-amber-50 rounded-md border border-amber-200">
                                                <p className="text-sm font-medium text-amber-800">Note:</p>
                                                <p className="text-sm text-amber-700">{selectedClient.note}</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Orders History */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-3">Historique des Commandes</h3>
                                    {loadingClient ? (
                                        <div className="flex justify-center py-8">
                                            <div className="spinner" />
                                        </div>
                                    ) : clientOrders.length === 0 ? (
                                        <p className="text-center py-8 text-muted-foreground">
                                            Aucune commande trouvée
                                        </p>
                                    ) : (
                                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                            {clientOrders.map((order) => (
                                                <Card key={order.id} className="hover:shadow-sm transition-shadow">
                                                    <CardContent className="p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-mono text-sm font-medium">
                                                                        {order.n_commande}
                                                                    </span>
                                                                    <Badge className={cn('status-badge text-xs', getStatusColor(order.status))}>
                                                                        {getStatusLabel(order.status)}
                                                                    </Badge>
                                                                </div>
                                                                <p className="text-sm text-muted-foreground mt-1">
                                                                    {order.produit}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {formatDate(order.date_commande)}
                                                                </p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-semibold">{formatCurrency(order.prix_total)}</p>
                                                            </div>
                                                        </div>
                                                        {order.observation && (
                                                            <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                                                                <span className="font-medium">Note:</span> {order.observation}
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <DialogFooter>
                            <Button
                                variant={selectedClient?.is_mauvais_client ? "outline" : "destructive"}
                                onClick={() => selectedClient && handleToggleMauvaisClient(selectedClient)}
                            >
                                {selectedClient?.is_mauvais_client ? (
                                    <>
                                        <UserCheck className="w-4 h-4 mr-2" />
                                        Retirer Tag Mauvais Client
                                    </>
                                ) : (
                                    <>
                                        <UserX className="w-4 h-4 mr-2" />
                                        Marquer comme Mauvais Client
                                    </>
                                )}
                            </Button>
                            <Button variant="outline" onClick={() => setClientDialogOpen(false)}>
                                Fermer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Note Dialog */}
                <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Note Client - {selectedClient?.full_name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Note</Label>
                                <Textarea
                                    value={clientNote}
                                    onChange={(e) => setClientNote(e.target.value)}
                                    placeholder="Ajouter une note pour ce client..."
                                    rows={4}
                                    className="input-luxury"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button onClick={handleSaveNote} disabled={savingNote} className="btn-luxury">
                                {savingNote ? 'Sauvegarde...' : 'Sauvegarder'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Import CSV Dialog */}
                <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                <FileSpreadsheet className="w-5 h-5 inline mr-2" />
                                Importer des clients
                            </DialogTitle>
                            <DialogDescription>
                                Importez vos anciens clients depuis un fichier CSV
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm font-medium mb-2">Format CSV attendu :</p>
                                <p className="text-xs text-muted-foreground">
                                    Colonnes reconnues : <code>phone</code>, <code>name</code>, <code>address</code>, <code>wilaya</code>
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Le numéro de téléphone est <strong>obligatoire</strong>.
                                </p>
                            </div>
                            
                            <div className="border-2 border-dashed rounded-lg p-6 text-center">
                                <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                                <label className="cursor-pointer">
                                    <span className="text-primary font-medium hover:underline">
                                        Cliquez pour sélectionner un fichier
                                    </span>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleImportCSV}
                                        className="hidden"
                                        disabled={importing}
                                    />
                                </label>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Fichier CSV uniquement
                                </p>
                            </div>
                            
                            {importing && (
                                <div className="flex items-center justify-center gap-2 text-primary">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    <span>Import en cours...</span>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setImportDialogOpen(false)} disabled={importing}>
                                Annuler
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
